// Finance Forecaster logo: the brand curve plus the wordmark.
//
// Two grounds, per the Brand Book:
//   onLight  — blue history stroke, green forecast stroke, Midnight "Finance",
//              green "Forecaster". For white and Mist backgrounds.
//   onBlue   — white history stroke, green forecast stroke, white "Finance",
//              green "Forecaster". For Finance Blue (and Midnight) backgrounds.
//
// The forecast stroke is never recoloured. Sizes respect the Brand Book minima
// (icon 48px, horizontal lockup 240px); `sm` is 48 on purpose. Clear space
// around the lockup is the cap height of the "F", applied as padding so the
// component can sit flush against its container.
import React from 'react';
import { BRAND, CURVE_BOX, FORECAST_PATH, HISTORY_PATH, MARK_STROKE, MARK_VIEWBOX } from './mark';

const GROUNDS = {
  onLight: { history: BRAND.blue, forecast: BRAND.green, finance: BRAND.midnight, forecaster: BRAND.green },
  onBlue: { history: BRAND.white, forecast: BRAND.green, finance: BRAND.white, forecaster: BRAND.green },
};

// Icon height → wordmark size. The two-line wordmark is set so its total
// height matches the icon; cap height of the "F" ≈ 0.7em at 800 weight.
const SIZES = {
  sm: { icon: 48, text: 19, gap: 12 },
  md: { icon: 56, text: 22, gap: 14 },
  lg: { icon: 72, text: 28, gap: 18 },
  xl: { icon: 96, text: 38, gap: 24 },
};

const resolveGround = (variant) => {
  if (variant === 'onBlue' || variant === 'white' || variant === 'dark') return GROUNDS.onBlue;
  return GROUNDS.onLight;
};

// The curve alone, filling a square box (no tile). Used by the lockup and as
// the line-only mark on light and blue grounds.
export const Curve = ({ size = 48, variant = 'onLight', className = '', title }) => {
  const g = resolveGround(variant);
  const { x, y, width, height } = CURVE_BOX;
  // Keep the curve's own proportions: it is wider than tall, so the box is
  // square and the curve sits centred vertically.
  const vb = `${x} ${y - (width - height) / 2} ${width} ${width}`;
  return (
    <svg
      width={size}
      height={size}
      viewBox={vb}
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
      className={className}
      role="img"
      aria-label={title || 'Finance Forecaster'}
    >
      <path d={HISTORY_PATH} stroke={g.history} strokeWidth={MARK_STROKE * 1.6} strokeLinecap="round" />
      <path d={FORECAST_PATH} stroke={g.forecast} strokeWidth={MARK_STROKE * 1.6} strokeLinecap="round" />
    </svg>
  );
};

// The app icon: the curve on a Finance Blue tile with rounded corners.
// Always white history + green forecast, regardless of the page ground.
export const Tile = ({ size = 48, className = '', radius = 0.22, title }) => (
  <svg
    width={size}
    height={size}
    viewBox={MARK_VIEWBOX}
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
    className={className}
    role="img"
    aria-label={title || 'Finance Forecaster'}
  >
    <rect width="1024" height="1024" rx={1024 * radius} fill={BRAND.blue} />
    <path d={HISTORY_PATH} stroke={BRAND.white} strokeWidth={MARK_STROKE * 2.4} strokeLinecap="round" />
    <path d={FORECAST_PATH} stroke={BRAND.green} strokeWidth={MARK_STROKE * 2.4} strokeLinecap="round" />
  </svg>
);

const Logo = ({ className = '', size = 'md', showText = true, variant = 'onLight', tagline = false }) => {
  const s = SIZES[size] || SIZES.md;
  const g = resolveGround(variant);
  const clearSpace = Math.round(s.text * 0.7);
  return (
    <span
      className={`inline-flex items-center ${className}`}
      style={{ gap: s.gap, padding: showText ? clearSpace : 0 }}
    >
      <Curve size={s.icon} variant={variant} />
      {showText && (
        <span className="flex flex-col" style={{ lineHeight: 1.02 }}>
          <span
            style={{ color: g.finance, fontSize: s.text, fontWeight: 800, letterSpacing: '-0.02em' }}
          >
            Finance
          </span>
          <span
            style={{ color: g.forecaster, fontSize: s.text, fontWeight: 800, letterSpacing: '-0.02em' }}
          >
            Forecaster
          </span>
          {tagline && (
            <span style={{ color: g.finance, fontSize: Math.round(s.text * 0.5), fontWeight: 500, marginTop: 6, opacity: 0.85 }}>
              See where your money is going. Together.
            </span>
          )}
        </span>
      )}
    </span>
  );
};

// Kept for existing imports: the compact mark is the tile.
export const LogoMark = ({ size = 48, variant = 'onLight' }) => (variant === 'onBlue' ? <Curve size={size} variant="onBlue" /> : <Tile size={size} />);

export default Logo;
